import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { logger } from 'config/logger';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useCreateSnackbar } from 'shared/legacy/contexts/SnackbarContext/useCreateSnackbar';
import { QueryKey } from 'types/query';
import { TranslateMessageParams, TranslateMessageResponse } from './useTranslateMessageMutation.types';

const TRANSLATION_CACHE_TIME = 1000 * 60 * 30;

export const getTranslateMessageCacheKey = ({ message, targetLanguage, subject }: TranslateMessageParams) => [
  QueryKey.useTranslateMessageMutation,
  { message, targetLanguage, subject: subject ?? null },
];

export const useTranslateMessageMutation = () => {
  const { dictionary } = useContext(LocalizationContext);
  const createSnackbar = useCreateSnackbar();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ message, targetLanguage, subject }: TranslateMessageParams): Promise<TranslateMessageResponse> => {
      // The same content translated to the same language always yields the same result, so it is
      // served from the cache instead of hitting the endpoint again.
      return queryClient.fetchQuery({
        queryKey: getTranslateMessageCacheKey({ message, targetLanguage, subject }),
        queryFn: async () => {
          const response = await campaignsClient.translateMessage({
            message,
            language: targetLanguage,
            subject,
          });

          return {
            translatedMessage: response.data.translated_message,
            originalMessage: response.data.original_message,
            targetLanguage: response.data.target_language,
            translatedSubject: response.data.translated_subject,
            originalSubject: response.data.original_subject,
          };
        },
        cacheTime: TRANSLATION_CACHE_TIME,
      });
    },
    {
      onError: (error) => {
        logger.error({ error, customMessage: 'Error while translating message' });

        createSnackbar(error instanceof Error ? error.message : dictionary.somethingWentWrong, {
          variant: 'danger',
          duration: 10_000,
        });
      },
      onSuccess: () => {
        createSnackbar(dictionary.messageTranslatedSuccessfully, {
          variant: 'success',
        });
      },
    },
  );
};
